const WASM_ASSET_PATH = "/mediapipe/wasm";
const FACE_MODEL_ASSET_PATH = "/mediapipe/models/face_landmarker.task";

export const RIDER_LIVENESS_METHOD = "mediapipe_active_challenge_v1";
export const RIDER_LIVENESS_CHALLENGES = Object.freeze(["blink", "mouth_open"]);

const toScoreMap = (classification) =>
  Object.fromEntries(
    (classification?.categories || []).map((category) => [
      category.categoryName,
      Number(category.score || 0),
    ])
  );

const getFaceBounds = (landmarks) => {
  if (!Array.isArray(landmarks) || !landmarks.length) return null;
  const xs = landmarks.map(({ x }) => Number(x));
  const ys = landmarks.map(({ y }) => Number(y));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

export const normalizeFaceLandmarkerResult = (result) => {
  const faces = Array.isArray(result?.faceLandmarks) ? result.faceLandmarks : [];
  if (faces.length !== 1) {
    return {
      faceCount: faces.length,
      faceReady: false,
      scores: {},
    };
  }

  const bounds = getFaceBounds(faces[0]);
  const centered =
    bounds &&
    bounds.width >= 0.18 &&
    bounds.width <= 0.78 &&
    bounds.height >= 0.22 &&
    bounds.height <= 0.9 &&
    bounds.centerX >= 0.26 &&
    bounds.centerX <= 0.74 &&
    bounds.centerY >= 0.26 &&
    bounds.centerY <= 0.74;

  return {
    faceCount: 1,
    faceReady: Boolean(centered),
    bounds,
    scores: toScoreMap(result?.faceBlendshapes?.[0]),
  };
};

export const createLivenessChallengeSequence = (randomValue = Math.random()) =>
  randomValue < 0.5
    ? [...RIDER_LIVENESS_CHALLENGES]
    : [...RIDER_LIVENESS_CHALLENGES].reverse();

export const createChallengeState = () => ({
  phase: "neutral",
  neutralFrames: 0,
  peakScore: 0,
});

export const advanceLivenessChallenge = (challenge, state, scores) => {
  const current = state || createChallengeState();
  const blinkScore = Math.min(
    Number(scores?.eyeBlinkLeft || 0),
    Number(scores?.eyeBlinkRight || 0)
  );
  const mouthScore = Number(scores?.jawOpen || 0);
  const actionScore = challenge === "blink" ? blinkScore : mouthScore;
  const neutralThreshold = challenge === "blink" ? 0.24 : 0.18;
  const actionThreshold = challenge === "blink" ? 0.48 : 0.38;
  const releaseThreshold = challenge === "blink" ? 0.28 : 0.23;

  if (current.phase === "neutral") {
    const neutralFrames = actionScore <= neutralThreshold ? current.neutralFrames + 1 : 0;
    return {
      completed: false,
      state: {
        ...current,
        neutralFrames,
        phase: neutralFrames >= 3 ? "waiting_action" : "neutral",
      },
    };
  }

  if (current.phase === "waiting_action") {
    if (actionScore >= actionThreshold) {
      return {
        completed: false,
        state: {
          ...current,
          phase: "waiting_release",
          peakScore: Math.max(current.peakScore, actionScore),
        },
      };
    }
    return { completed: false, state: current };
  }

  if (current.phase === "waiting_release" && actionScore <= releaseThreshold) {
    return {
      completed: true,
      score: Math.max(0, Math.min(1, current.peakScore)),
      state: current,
    };
  }

  return {
    completed: false,
    state: {
      ...current,
      peakScore: Math.max(current.peakScore, actionScore),
    },
  };
};

export const getLivenessChallengeCopy = (challenge, phase) => {
  if (phase === "neutral") return "Mira de frente y mantén el rostro quieto";
  if (challenge === "blink") {
    return phase === "waiting_release" ? "Ahora abre los ojos" : "Parpadea una vez";
  }
  return phase === "waiting_release" ? "Ahora cierra la boca" : "Abre la boca";
};

export const createFaceLivenessDetector = async () => {
  const { FaceLandmarker, FilesetResolver } = await import(
    "@mediapipe/tasks-vision"
  );
  const vision = await FilesetResolver.forVisionTasks(WASM_ASSET_PATH);
  const landmarker = await FaceLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: FACE_MODEL_ASSET_PATH,
    },
    runningMode: "VIDEO",
    numFaces: 2,
    minFaceDetectionConfidence: 0.65,
    minFacePresenceConfidence: 0.65,
    minTrackingConfidence: 0.6,
    outputFaceBlendshapes: true,
  });

  return {
    analyze(video, timestamp) {
      return normalizeFaceLandmarkerResult(
        landmarker.detectForVideo(video, timestamp)
      );
    },
    close() {
      landmarker.close();
    },
  };
};
